/**
 * Shared formatter utilities
 *
 * Pure functions for formatting data consistently across the application.
 * These functions are framework-agnostic and easily testable.
 */

import i18n from '@core/i18n';

/**
 * Format bytes to human-readable file size
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "2.5 MB")
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(1048576) // "1 MB"
 */
export function formatFileSize(bytes) {
  if (!bytes || bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format user object to display name
 *
 * @param {Object} user - User object
 * @param {string} [user.first_name] - User's first name
 * @param {string} [user.last_name] - User's last name
 * @param {string} [user.username] - User's username
 * @param {Function} t - Translation function
 * @returns {string} Formatted user name
 *
 * @example
 * formatUploader({ first_name: 'John', last_name: 'Doe' }, t) // "John Doe"
 * formatUploader({ username: 'johndoe' }, t) // "johndoe"
 * formatUploader(null, t) // "Unknown" (translated)
 */
export function formatUploader(user, t) {
  if (!user) {
    return t('file.unknownUploader', 'Unknown');
  }

  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return fullName || user.username || t('file.unknownUploader', 'Unknown');
}

/**
 * Format ISO date string to compact date/time (dd/mm/yyyy, hh:mm)
 *
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted compact date string
 *
 * @example
 * formatDateCompact('2025-01-15T10:30:00Z') // "15/01/2025, 10:30"
 */
export function formatDateCompact(dateString) {
  if (!dateString) return '';

  const locale = i18n.language || 'en';

  return new Date(dateString).toLocaleString(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format ISO date string to localized date/time
 *
 * @param {string} dateString - ISO date string
 * @param {Object} [options] - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 *
 * @example
 * formatDate('2025-01-15T10:30:00Z') // "January 15, 2025, 10:30 AM"
 * formatDate('2025-01-15T10:30:00Z', { dateStyle: 'short' }) // "1/15/25"
 */
export function formatDate(dateString, options = {}) {
  if (!dateString) return '';

  const defaultOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };

  // Use the current i18n language for localization
  const locale = i18n.language || 'en';

  return new Date(dateString).toLocaleString(locale, {
    ...defaultOptions,
    ...options,
  });
}

/**
 * Format date string to short format (for mobile cards)
 *
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted short date string
 *
 * @example
 * formatDateShort('2025-01-15T10:30:00Z') // "Jan 15, 2025, 10:30 AM"
 */
export function formatDateShort(dateString) {
  if (!dateString) return '';

  // Use the current i18n language for localization
  const locale = i18n.language || 'en';

  return new Date(dateString).toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format ISO date string to human-friendly relative time (e.g., "2 days ago")
 *
 * @param {string} dateString - ISO date string
 * @returns {string} Human-friendly relative time string
 *
 * @example
 * formatRelativeTime('2025-01-15T10:30:00Z') // "2 days ago"
 * formatRelativeTime('2025-01-16T10:30:00Z') // "1 day ago"
 * formatRelativeTime('2025-01-16T20:30:00Z') // "14 hours ago"
 * formatRelativeTime('2025-01-16T22:45:00Z') // "12 minutes ago"
 */
export function formatRelativeTime(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return diffSeconds <= 1
      ? i18n.t('time.justNow', 'just now')
      : i18n.t('time.secondsAgo', '{{count}} seconds ago', { count: diffSeconds });
  }

  if (diffMinutes < 60) {
    return i18n.t('time.minutesAgo', '{{count}} minutes ago', { count: diffMinutes });
  }

  if (diffHours < 24) {
    return i18n.t('time.hoursAgo', '{{count}} hours ago', { count: diffHours });
  }

  if (diffDays < 7) {
    return i18n.t('time.daysAgo', '{{count}} days ago', { count: diffDays });
  }

  // For older dates, show weeks or fall back to formatted date
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) {
    return i18n.t('time.weeksAgo', '{{count}} weeks ago', { count: diffWeeks });
  }

  // For very old dates, show months
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return i18n.t('time.monthsAgo', '{{count}} months ago', { count: diffMonths });
  }

  // For very very old dates, show years
  const diffYears = Math.floor(diffDays / 365);
  return i18n.t('time.yearsAgo', '{{count}} years ago', { count: diffYears });
}

/**
 * Format duration between two timestamps in human-friendly format
 *
 * @param {string} startDateString - ISO date string for start time
 * @param {string} endDateString - ISO date string for end time
 * @returns {string} Human-friendly duration string
 *
 * @example
 * formatDuration('2025-01-15T10:00:00Z', '2025-01-15T10:15:00Z') // "15 minutes"
 * formatDuration('2025-01-15T10:00:00Z', '2025-01-15T12:30:00Z') // "2 hours 30 minutes"
 * formatDuration('2025-01-15T10:00:00Z', '2025-01-16T10:00:00Z') // "1 day"
 */
export function formatDuration(startDateString, endDateString) {
  if (!startDateString || !endDateString) return '';

  const start = new Date(startDateString);
  const end = new Date(endDateString);
  const diffMs = end - start;

  // Handle invalid or negative durations
  if (diffMs < 0) return '';

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Less than a minute
  if (diffSeconds < 60) {
    return diffSeconds <= 1
      ? i18n.t('time.duration.second', '1 second')
      : i18n.t('time.duration.seconds', '{{count}} seconds', { count: diffSeconds });
  }

  // Less than an hour - show minutes
  if (diffMinutes < 60) {
    return diffMinutes === 1
      ? i18n.t('time.duration.minute', '1 minute')
      : i18n.t('time.duration.minutes', '{{count}} minutes', { count: diffMinutes });
  }

  // Less than a day - show hours and minutes
  if (diffHours < 24) {
    const remainingMinutes = diffMinutes % 60;
    if (diffHours === 1 && remainingMinutes === 0) {
      return i18n.t('time.duration.hour', '1 hour');
    }
    if (diffHours === 1) {
      const minutesPart =
        remainingMinutes === 1
          ? i18n.t('time.minute', 'minute')
          : i18n.t('time.minutes', 'minutes');
      return i18n.t(
        'time.duration.hourMinutes',
        '1 hour {{minutes}} {{minutesLabel}}',
        { minutes: remainingMinutes, minutesLabel: minutesPart }
      );
    }
    if (remainingMinutes === 0) {
      return i18n.t('time.duration.hours', '{{count}} hours', { count: diffHours });
    }
    const minutesPart =
      remainingMinutes === 1 ? i18n.t('time.minute', 'minute') : i18n.t('time.minutes', 'minutes');
    return i18n.t(
      'time.duration.hoursMinutes',
      '{{hours}} hours {{minutes}} {{minutesLabel}}',
      { hours: diffHours, minutes: remainingMinutes, minutesLabel: minutesPart }
    );
  }

  // Days - show days and hours
  const remainingHours = diffHours % 24;
  if (diffDays === 1 && remainingHours === 0) {
    return i18n.t('time.duration.day', '1 day');
  }
  if (diffDays === 1) {
    const hoursPart =
      remainingHours === 1 ? i18n.t('time.hour', 'hour') : i18n.t('time.hours', 'hours');
    return i18n.t('time.duration.dayHours', '1 day {{hours}} {{hoursLabel}}', {
      hours: remainingHours,
      hoursLabel: hoursPart,
    });
  }
  if (remainingHours === 0) {
    return i18n.t('time.duration.days', '{{count}} days', { count: diffDays });
  }
  const hoursPart =
    remainingHours === 1 ? i18n.t('time.hour', 'hour') : i18n.t('time.hours', 'hours');
  return i18n.t('time.duration.daysHours', '{{days}} days {{hours}} {{hoursLabel}}', {
    days: diffDays,
    hours: remainingHours,
    hoursLabel: hoursPart,
  });
}

/**
 * Helper function to format days as months and days
 *
 * @param {number} days - Number of days
 * @returns {string} Formatted string (e.g., "1 mes y 15 días", "2 meses")
 */
function formatDaysAsMonthsAndDays(days) {
  if (days < 30) {
    // Less than 30 days, show only days
    return days === 1
      ? i18n.t('time.delay.day', '1 día')
      : i18n.t('time.delay.days', '{{count}} días', { count: days });
  }

  const months = Math.floor(days / 30);
  const remainingDays = days % 30;
  const monthLabel = months === 1 ? 'mes' : 'meses';

  if (remainingDays === 0) {
    // Only complete months
    return `${months} ${monthLabel}`;
  }

  // Months + days
  const dayLabel = remainingDays === 1 ? 'día' : 'días';
  return `${months} ${monthLabel} y ${remainingDays} ${dayLabel}`;
}

/**
 * Parse timedelta string to seconds for calculation purposes
 *
 * @param {string|number|null} delay - Django timedelta string or seconds
 * @returns {number|null} Total seconds, or null if invalid
 *
 * @example
 * parseDelayToSeconds("90 00:00:00") // 7776000 (90 days in seconds)
 * parseDelayToSeconds("2:00:00") // 7200 (2 hours in seconds)
 * parseDelayToSeconds(3600) // 3600
 */
function parseDelayToSeconds(delay) {
  if (!delay) return null;

  // Handle numeric seconds directly
  if (typeof delay === 'number') {
    return delay;
  }

  // Handle string format
  if (typeof delay === 'string') {
    // Match DRF ISO 8601 format: "D HH:MM:SS" (e.g., "90 00:00:00" for 90 days)
    const iso8601Match = delay.match(/^(\d+)\s+(\d+):(\d+):(\d+)$/);
    if (iso8601Match) {
      const days = parseInt(iso8601Match[1], 10);
      const hours = parseInt(iso8601Match[2], 10);
      const minutes = parseInt(iso8601Match[3], 10);
      const seconds = parseInt(iso8601Match[4], 10);
      return days * 86400 + hours * 3600 + minutes * 60 + seconds;
    }

    // Match Django verbose format: "1 day, 0:00:00"
    const dayMatch = delay.match(/(\d+)\s+day/);
    const timeMatch = delay.match(/(\d+):(\d+):(\d+)/);

    let totalSeconds = 0;
    if (dayMatch) {
      totalSeconds += parseInt(dayMatch[1], 10) * 86400;
    }
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);
      const seconds = parseInt(timeMatch[3], 10);
      totalSeconds += hours * 3600 + minutes * 60 + seconds;
    }

    if (totalSeconds > 0) {
      return totalSeconds;
    }

    // Match time-only format: "HH:MM:SS"
    const timeOnlyMatch = delay.match(/^(\d+):(\d+):(\d+)$/);
    if (timeOnlyMatch) {
      const hours = parseInt(timeOnlyMatch[1], 10);
      const minutes = parseInt(timeOnlyMatch[2], 10);
      const seconds = parseInt(timeOnlyMatch[3], 10);
      return hours * 3600 + minutes * 60 + seconds;
    }
  }

  return null;
}

/**
 * Format time remaining until a step can be started
 *
 * Calculates the actual time remaining based on when the previous step was completed
 * and the configured delay. Shows "Disponible ahora" if the delay has already passed.
 *
 * @param {string|null} completedAt - ISO date string when previous step was completed
 * @param {string|number|null} delay - Configured delay (Django timedelta or seconds)
 * @returns {string|null} Human-friendly time remaining string or null
 *
 * @example
 * formatTimeRemaining('2025-01-15T10:00:00Z', '90 00:00:00') // "2 meses y 25 días"
 * formatTimeRemaining('2025-01-15T10:00:00Z', '2:00:00') // "1 hora y 45 minutos"
 * formatTimeRemaining('2020-01-15T10:00:00Z', '1 00:00:00') // "Disponible ahora"
 * formatTimeRemaining(null, '90 00:00:00') // null (fallback to static delay)
 */
export function formatTimeRemaining(completedAt, delay) {
  // If no completed_at, can't calculate remaining time
  if (!completedAt) return null;

  // Parse delay to seconds
  const delaySeconds = parseDelayToSeconds(delay);
  if (!delaySeconds) return null;

  // Calculate target date: completed_at + delay
  const completedDate = new Date(completedAt);
  const targetDate = new Date(completedDate.getTime() + delaySeconds * 1000);

  // Calculate time remaining: target - now
  const now = new Date();
  const remainingMs = targetDate - now;

  // If delay has already passed, step is available
  if (remainingMs <= 0) {
    return i18n.t('time.availableNow', 'Disponible ahora');
  }

  // Convert remaining milliseconds to human-friendly format
  // Calculate each unit independently to avoid rounding error accumulation
  const remainingSeconds = Math.floor(remainingMs / 1000);
  const remainingMinutes = Math.floor(remainingMs / (1000 * 60));
  const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
  const remainingDays = Math.floor(remainingMs / (1000 * 60 * 60 * 24));

  // Format based on magnitude
  if (remainingDays >= 30) {
    // Use months format
    return formatDaysAsMonthsAndDays(remainingDays);
  }

  if (remainingDays > 0) {
    // Days format
    return remainingDays === 1
      ? i18n.t('time.delay.day', '1 día')
      : i18n.t('time.delay.days', '{{count}} días', { count: remainingDays });
  }

  if (remainingHours > 0) {
    // Hours format
    return remainingHours === 1
      ? i18n.t('time.delay.hour', '1 hora')
      : i18n.t('time.delay.hours', '{{count}} horas', { count: remainingHours });
  }

  // Minutes format
  return remainingMinutes === 1
    ? i18n.t('time.delay.minute', '1 minuto')
    : i18n.t('time.delay.minutes', '{{count}} minutos', { count: remainingMinutes });
}

/**
 * Format timedelta/duration to human-friendly format
 *
 * Used for displaying configured delays between workflow steps.
 * Converts 30+ days to months format for better readability.
 *
 * @param {string|number|null} delay - Django timedelta string (e.g., "1 day, 0:00:00") or seconds
 * @returns {string} Human-friendly delay string (e.g., "2 días", "1 mes y 15 días", "3 horas")
 *
 * @example
 * formatDelay("1 day, 0:00:00") // "1 día"
 * formatDelay("45 00:00:00") // "1 mes y 15 días"
 * formatDelay("60 00:00:00") // "2 meses"
 * formatDelay("0:02:00") // "2 horas"
 * formatDelay(3600) // "1 hora" (if numeric seconds)
 */
export function formatDelay(delay) {
  if (!delay) return '';

  // Handle Django timedelta string format (e.g., "1 day, 0:00:00" or "2 days, 3:30:00")
  if (typeof delay === 'string') {
    // Match DRF ISO 8601 format: "D HH:MM:SS" (e.g., "2 00:00:00" for 2 days)
    // This must be checked BEFORE the generic time regex to avoid misinterpretation
    const iso8601Match = delay.match(/^(\d+)\s+(\d+):(\d+):(\d+)$/);
    if (iso8601Match) {
      const days = parseInt(iso8601Match[1], 10);
      // Only use this pattern if days > 0, otherwise fall through to time-only pattern
      if (days > 0) {
        return formatDaysAsMonthsAndDays(days);
      }
    }

    // Match Django verbose format: "1 day" or "2 days"
    const dayMatch = delay.match(/(\d+)\s+day/);
    if (dayMatch) {
      const days = parseInt(dayMatch[1], 10);
      return formatDaysAsMonthsAndDays(days);
    }

    // Match time-only format: "HH:MM:SS" (e.g., "02:00:00" for 2 hours)
    const timeMatch = delay.match(/^(\d+):(\d+):(\d+)$/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = parseInt(timeMatch[2], 10);

      if (hours > 0) {
        return hours === 1
          ? i18n.t('time.delay.hour', '1 hora')
          : i18n.t('time.delay.hours', '{{count}} horas', { count: hours });
      }

      if (minutes > 0) {
        return minutes === 1
          ? i18n.t('time.delay.minute', '1 minuto')
          : i18n.t('time.delay.minutes', '{{count}} minutos', { count: minutes });
      }
    }
  }

  // Handle numeric seconds
  if (typeof delay === 'number') {
    const days = Math.floor(delay / 86400);
    if (days > 0) {
      return formatDaysAsMonthsAndDays(days);
    }

    const hours = Math.floor(delay / 3600);
    if (hours > 0) {
      return hours === 1
        ? i18n.t('time.delay.hour', '1 hora')
        : i18n.t('time.delay.hours', '{{count}} horas', { count: hours });
    }

    const minutes = Math.floor(delay / 60);
    if (minutes > 0) {
      return minutes === 1
        ? i18n.t('time.delay.minute', '1 minuto')
        : i18n.t('time.delay.minutes', '{{count}} minutos', { count: minutes });
    }
  }

  // Fallback
  return delay.toString();
}
